// Auth + RBAC types. Client-side scaffold — see AuthContext for the disclaimer
// that enforcement is UX-only until a backend with Entra ID lands.

export type Role = 'Admin' | 'Manager' | 'Paradox' | 'Viewer';

export const ALL_ROLES: Role[] = ['Admin', 'Manager', 'Paradox', 'Viewer'];

// Roles whose permissions are editable by Admins. Admin itself is hard-coded
// to "everything" so an Admin can't accidentally lock all admins out.
export type EditableRole = Exclude<Role, 'Admin'>;
export const EDITABLE_ROLES: EditableRole[] = ['Manager', 'Paradox', 'Viewer'];

export interface AppUser {
  firstName: string;
  lastName: string;
  email: string;
  role: Role;
}

// One id per Tool defined in constants/tools.ts. `user-management` is the new
// system module — admins always see it; non-admins never do.
export type ModuleId =
  | 'mto-configurator'
  | 'flame-cell-calculator'
  | 'production-planning'
  | 'threaded-inline-arrestors'
  | 'tribal-knowledge'
  | 'laser-quote'
  | 'balanced-strategic-plan'
  | 'user-management';

// Permissions matrix: which modules each editable role can access.
export type RolePermissions = Record<EditableRole, ModuleId[]>;

// The whole RBAC configuration, persisted as one shared document (backend
// /rbac-config, localStorage cache). `userRoleOverrides` maps a user's identity
// (the RoleContext appUser.email, lowercased — for the Flameco login path this is
// `<internalId>@netsuite.flameco`) to an assigned role, layered on top of the
// login-derived base role. `knownModules` records which modules existed when the
// config was last saved, so newly-shipped modules can be auto-added to defaults.
export interface RbacConfig {
  rolePermissions: RolePermissions;
  userRoleOverrides: Record<string, Role>;
  knownModules?: ModuleId[];
}

export const fullName = (u: { firstName: string; lastName: string }): string =>
  `${u.firstName} ${u.lastName}`.trim();
